// Product Activity Tracker
// This utility tracks when products are modified to enable smart sorting

import { kvGet, kvSet } from '../infrastructure/storage/kvAccess';

const ACTIVITY_KEY = 'product_activity_tracker';

// Get product activity data from KV
export const getProductActivityData = () => {
  try {
    const data = kvGet(ACTIVITY_KEY);
    if (data && typeof data === 'object' && !Array.isArray(data)) return data;
    if (typeof data === 'string') {
      try {
        const p = JSON.parse(data);
        return p && typeof p === 'object' && !Array.isArray(p) ? p : {};
      } catch {
        return {};
      }
    }
    return {};
  } catch (error) {
    console.error('Error loading product activity data:', error);
    return {};
  }
};

// Save product activity data to KV
export const saveProductActivityData = (data) => {
  try {
    kvSet(ACTIVITY_KEY, data);
  } catch (error) {
    console.error('Error saving product activity data:', error);
  }
};

// Record activity for a product
export const recordProductActivity = (productId, activityType = 'modified') => {
  const now = Date.now();
  const activityData = getProductActivityData();
  
  activityData[productId] = {
    lastActivity: now,
    lastActivityType: activityType,
    lastModified: now
  };
  
  saveProductActivityData(activityData);
};

// Record multiple product activities (for bulk operations)
export const recordMultipleProductActivities = (productIds, activityType = 'modified') => {
  const now = Date.now();
  const activityData = getProductActivityData();
  
  productIds.forEach(productId => {
    activityData[productId] = {
      lastActivity: now,
      lastActivityType: activityType,
      lastModified: now
    };
  });
  
  saveProductActivityData(activityData);
};

// Get last activity timestamp for a product
export const getProductLastActivity = (productId) => {
  const activityData = getProductActivityData();
  return activityData[productId]?.lastActivity || 0;
};

// Get last activity type for a product
export const getProductLastActivityType = (productId) => {
  const activityData = getProductActivityData();
  return activityData[productId]?.lastActivityType || 'unknown';
};

// Sort products by last activity (most recent first)
export const sortProductsByActivity = (products) => {
  return [...products].sort((a, b) => {
    const aActivity = getProductLastActivity(a.id);
    const bActivity = getProductLastActivity(b.id);
    return bActivity - aActivity; // Most recent first
  });
};

// Sort products with out-of-stock priority, then by activity
export const sortProductsByStockAndActivity = (products, getStockQuantity) => {
  return [...products].sort((a, b) => {
    const aStock = getStockQuantity(a);
    const bStock = getStockQuantity(b);
    
    // First priority: out of stock products
    if (aStock === 0 && bStock > 0) return -1;
    if (bStock === 0 && aStock > 0) return 1;
    
    // Second priority: by last activity (most recent first)
    const aActivity = getProductLastActivity(a.id);
    const bActivity = getProductLastActivity(b.id);
    return bActivity - aActivity;
  });
};

// Activity types for better tracking
export const ACTIVITY_TYPES = {
  CREATED: 'created',
  MODIFIED: 'modified',
  SOLD: 'sold',
  PURCHASED: 'purchased',
  TRANSFERRED: 'transferred',
  STOCK_ADJUSTED: 'stock_adjusted',
  DELETED: 'deleted'
};
